<template>
  <v-container fluid grid-list-md>
    <v-layout row wrap>
      <v-flex xs12 lg12>
        <v-card>
          <v-card-title primary-title>
            <v-flex xs12 md9 lg9>
              <div class="headline">Vos indicateurs</div>
            </v-flex>
            <v-flex xs12 md3 lg3 v-if="isAdminOrSuperAdmin">
              <FormLineField
                :item="userField"
                :value="this.currentUser"
                @input="onChangeUser"
              />
            </v-flex>
          </v-card-title>
          <v-card-text>
            <v-card>
              <v-card-title primary-title>
                <v-flex xs12 md9 lg9>
                  <div class="headline">Graphiques évolution</div>
                </v-flex>
                <v-flex xs12 md3 lg3 v-if="isAdminOrSuperAdmin">
                  <FormLineField
                    :item="dateField"
                    :value="this.from"
                    @input="onChangeDate"
                    @clear="onDateClear"
                  />
                </v-flex>
              </v-card-title>
              <v-card-text>
                <v-layout row wrap>
                  <v-flex xs12 lg6>
                    <Bar
                      :chart-options="chartOptions.evolution"
                      :chart-data="creationStoppedChartData"
                    />
                  </v-flex>
                  <v-flex xs12 lg6>
                    <Bar
                      :chart-options="chartOptions.evolution"
                      :chart-data="toAssignResearchStartedChartData"
                    />
                  </v-flex>
                  <v-flex
                    :xs12="isCurrentUserAdmin"
                    :lg6="isCurrentUserAdmin"
                    :lg12="!isCurrentUserAdmin"
                  >
                    <Bar
                      :chart-options="chartOptions.evolution"
                      :chart-data="leaseFetchedSentToALChartData"
                    />
                  </v-flex>
                  <v-flex xs12 lg6 v-if="isCurrentUserAdmin">
                    <Bar
                      :chart-options="chartOptions.evolution"
                      :chart-data="paidChartData"
                    />
                  </v-flex> </v-layout></v-card-text
            ></v-card>
            <v-card>
              <v-card-title primary-title>
                <div class="headline">Graphique proportions</div>
              </v-card-title>
              <v-card-text>
                <Bar
                  :chart-options="chartOptions.proportion"
                  :chart-data="proportionChartData"
                />
              </v-card-text>
            </v-card>
            <v-card v-if="isAdminOrSuperAdmin">
              <v-card-title primary-title>
                <div class="headline">Graphique B2C</div>
              </v-card-title>
              <v-card-text>
                <LineChartGenerator
                  :chart-options="chartOptions.b2c"
                  :chart-data="b2cChartData"
                />
              </v-card-text>
            </v-card>
            <v-card>
              <v-card-title primary-title>
                <div class="headline">Chiffres globaux</div>
              </v-card-title>
              <v-card-text>
                <v-data-table
                  v-for="year in years"
                  :key="`stats-table-${year}`"
                  :headers="tableHeaders(year)"
                  :items="tableStats(year)"
                  disable-initial-sort
                  hide-actions
                >
                  <template v-slot:headerCell="cell">
                    <template v-if="cell.header.text">
                      <template v-if="cell.header.text === 'Total'">
                        {{ cell.header.text }}
                      </template>
                      <template v-else>
                        {{ cell.header.text | monthYear }}
                      </template>
                    </template>
                  </template>
                  <template v-slot:items="props">
                    <td
                      v-for="(cell, i) in props.item"
                      :key="`row-${year}-${props.index}-${i}`"
                      :class="{
                        'text-xs-right': i > 0
                      }"
                    >
                      {{ cell }}
                    </td>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import "chartjs-adapter-moment";
import FormLineField from "@/components/commons/FormLineField.vue";
import { ROLES } from "@/data/DefaultSettings";
import { fetchHil2 } from "@/utils";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip
} from "chart.js";
import _ from "lodash";
import moment from "moment";
import { Bar, Line as LineChartGenerator } from "vue-chartjs/legacy";
import { mapGetters, mapState } from "vuex";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LineElement,
  LinearScale,
  TimeScale,
  ChartDataLabels
);

export default {
  name: "Dashboard",
  components: {
    FormLineField,
    Bar,
    LineChartGenerator
  },
  data() {
    return {
      rawData: null,
      from: {
        value: moment()
          .subtract(1, "years")
          .startOf("year")
          .toISOString()
      },
      to: {
        value: moment()
          .endOf("month")
          .toISOString()
      },
      b2c: {
        value: "day"
      },
      chartOptions: {
        evolution: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            ...Object.assign(
              {},
              ..._.times(4, index => ({
                [`x${index}`]: {
                  type: "time",
                  time: {
                    parser: "YYYY-MM",
                    displayFormats: {
                      month: "MM/YYYY"
                    },
                    unit: "month"
                  },
                  display: index === 0
                }
              }))
            ),
            y: {
              beginAtZero: true,
              grace: "15%",
              ticks: {
                stepSize: 10
              }
            }
          },
          plugins: {
            legend: {
              display: true,
              labels: {
                filter(legendItem, chartData) {
                  return (
                    chartData.datasets[legendItem.datasetIndex].isFocus !== true
                  );
                }
              }
            },
            tooltip: {
              callbacks: {
                label(context) {
                  return `${context.dataset.label}: ${context.raw.count}`;
                },
                title(context) {
                  return moment(context[0].label).format("MM/YYYY");
                }
              }
            },
            datalabels: {
              color: context => {
                return context.dataset.order % 2 ? "#FFF" : "rgb(117,117,117)";
              },
              anchor: "end",
              align: context => (context.dataset.order % 2 ? "start" : "top"),
              display(context) {
                if (
                  context.chart.data.datasets.length > 2 &&
                  context.datasetIndex % 2
                ) {
                  return false;
                }

                return context.dataset.data[context.dataIndex].count !== 0;
              },
              formatter(value) {
                return value.count;
              }
            }
          },
          onClick: this.onGraphClick
        },
        proportion: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: "time",
              time: {
                parser: "YYYY-MM",
                displayFormats: {
                  month: "MM/YYYY"
                },
                unit: "month"
              }
            },
            y: {
              min: 0,
              max: 100,
              ticks: {
                stepSize: 10,
                callback(value) {
                  return `${value}%`;
                }
              }
            }
          },
          plugins: {
            tooltip: {
              callbacks: {
                label(context) {
                  return `${context.dataset.label}: ${context.raw.y}%`;
                },
                title(context) {
                  return moment(context[0].label).format("MM/YYYY");
                }
              }
            },
            datalabels: {
              align: "top",
              anchor: "end",
              color: "rgb(117,117,117)",
              display(context) {
                return context.dataset.data[context.dataIndex].y !== 0;
              },
              formatter(value) {
                return `${value.y}%`;
              }
            }
          }
        },
        b2c: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: "time",
              time: {
                parser: "YYYYMM",
                displayFormats: {
                  month: "MM/YYYY"
                },
                unit: "month"
              }
            },
            y: {
              ticks: {
                stepSize: 10
              }
            }
          },
          plugins: {
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                title(context) {
                  return moment(context[0].label).format("MM/YYYY");
                },
                afterBody(context) {
                  return `\nTrigrammes: ${_(context)
                    .flatMap(
                      ({ dataset, dataIndex }) =>
                        dataset.data[dataIndex].trigrams
                    )
                    .uniq()
                    .sortBy()
                    .value()
                    .join(", ")}`;
                }
              }
            },
            datalabels: {
              display: false
            }
          }
        }
      },
      userField: {
        name: "value",
        as: "staff"
      },
      currentUser: {
        value: null
      },
      dateField: {
        name: "value",
        as: "month-year",
        mode: "start",
        clearable: true
      },
      dateManuallyChanged: false,
      focus: null
    };
  },
  async mounted() {
    this.initializeTimeRange();
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.rawData = await fetchHil2("post", `/reports/dashboard`, {
        body: {
          startDate: this.from.value,
          endDate: this.to.value,
          ...(this.currentUser.value ? { as: this.currentUser.value } : null),
          ...(this.focus ? { focus: this.focus } : null)
        }
      });
    },
    initializeTimeRange: function() {
      if (this.dateManuallyChanged) {
        return;
      }

      if (this.isCurrentUserAdmin) {
        this.from.value = moment()
          .subtract(1, "years")
          .startOf("year")
          .toISOString();
        this.to.value = moment()
          .endOf("year")
          .toISOString();
      } else {
        let from = moment()
          .subtract(1, "years")
          .startOf("year");
        if (from.isBefore(this.currentUserCreatedAt)) {
          from = moment(this.currentUserCreatedAt);
        }
        this.from.value = from.toISOString();
        this.to.value = moment()
          .endOf("year")
          .toISOString();
      }
    },
    async onChangeUser($event) {
      this.currentUser.value = $event.value;
      this.initializeTimeRange();
      await this.fetchData();
    },
    async onChangeDate() {
      this.dateManuallyChanged = true;
      await this.fetchData();
    },
    async onDateClear() {
      this.dateManuallyChanged = false;
      this.initializeTimeRange();
      await this.fetchData();
    },
    async onGraphClick(event, activeElements, chart) {
      if (activeElements.length) {
        const activeElement = activeElements[0];
        const month = chart.data.labels[activeElement.index];
        const status = chart.data.datasets[activeElement.datasetIndex].id;
        if (
          this.focus &&
          this.focus.month === month &&
          this.focus.status === status
        ) {
          return;
        }
        this.focus = {
          month: month,
          status: status
        };
      } else {
        this.focus = null;
        if (this.rawData?.focusStats) {
          this.rawData = _.omit(this.rawData, "focusStats");
        }
        return;
      }
      await this.fetchData();
    },
    createTableDataForStatus(year, key) {
      const buckets = this.allBucketsPerYear[year];
      const data = _.get(this.rawDataByTS, key);
      const currentMonth = moment().format("YYYY-MM");
      let total = 0;
      return [
        ...buckets.map(ts => {
          if (ts > currentMonth) {
            return ""; // We don't show the future
          }

          if (data[ts]) {
            const count = data[ts].count;
            total += count;
            return count;
          }

          return 0;
        }),
        total // above map will happen before this is inserted into the array
      ];
    },
    createChartDataForStatus(first, second, focus) {
      if (!this.rawData) {
        return [];
      }

      const previousValues = new Map();
      const hasFocusStats = Boolean(this.rawData.focusStats);
      const maxOrder = hasFocusStats ? 3 : 1;

      let axisIndex = 0;
      return [second, first] // Do the second before the first so we can remove the counts from one another
        .flatMap(({ status, label, color }, index, arr) => {
          const dataByTS = this.rawDataByTS.stats[status];
          const data = [];
          const focusData = [];

          this.buckets.graph.forEach(ts => {
            const count = dataByTS[ts] ? dataByTS[ts].count : 0;
            const focusCount =
              (hasFocusStats &&
                this.rawDataByTS.focusStats[status][ts]?.count) ||
              0;
            const previousValue = previousValues.get(ts) || 0;
            previousValues.set(ts, count + previousValue);
            const ratio = focusCount / count;
            const breakpoint = previousValue + (count - previousValue) * ratio;
            data.push({
              x: ts,
              y: hasFocusStats ? [count, breakpoint] : [count, previousValue],
              count
            });
            if (hasFocusStats) {
              focusData.push({
                x: ts,
                y: [breakpoint, previousValue],
                count: focusCount
              });
            }
          });

          return [
            {
              id: focus,
              label,
              backgroundColor: hasFocusStats
                ? color.replace("rgb(", "rgba(").replace(")", ",0.3)")
                : color,
              data,
              xAxisID: `x${axisIndex++}`,
              order: maxOrder - index
            },
            ...(hasFocusStats
              ? [
                  {
                    id: focus,
                    label: `${label} (sélection)`,
                    backgroundColor: color,
                    data: focusData,
                    xAxisID: `x${axisIndex++}`,
                    order: maxOrder - index + arr.length,
                    isFocus: true
                  }
                ]
              : [])
          ];
        })
        .reverse();
    },
    tableHeaders(year) {
      return [
        { text: "", value: "", sortable: false },
        ...this.allBucketsPerYear[year].map(timestamp => ({
          text: timestamp,
          value: timestamp,
          sortable: false,
          align: "right"
        })),
        { text: "Total", value: "Total", sortable: false }
      ];
    },
    tableStats(year) {
      if (!this.rawData) {
        return [];
      }

      const rows = [
        ["Inscrits", "stats.created"],
        ["Répartis", "stats.toAssign"],
        ["Recherches démarrées", "stats.researchStarted"],
        ["Baux signés", "stats.leaseFetched"],
        ["Envoyés à AL", "stats.sentToAL"]
      ];
      return rows.map(([desc, field]) => [
        desc,
        ...this.createTableDataForStatus(year, field)
      ]);
    }
  },
  computed: {
    ...mapState("autocompletes", {
      staffByTrigram: state => _.keyBy(state.staff, "trigram")
    }),
    ...mapState("user", ["role", "trigram", "createdAt"]),
    ...mapGetters("user", ["isAdminOrSuperAdmin"]),
    rawDataByTS() {
      if (!this.rawData) {
        return { stats: {}, focusStats: {} };
      }

      return _.mapValues(this.rawData, data =>
        _.mapValues(data, status => _.keyBy(status, "_id"))
      );
    },
    isCurrentUserAdmin() {
      const role = this.currentUser.value
        ? this.staffByTrigram[this.currentUser.value].role
        : this.role;
      return role === ROLES.SUPERADMIN || role === ROLES.ADMIN;
    },
    currentUserCreatedAt() {
      return this.currentUser.value
        ? this.staffByTrigram[this.currentUser.value].createdAt
        : this.createdAt;
    },
    buckets() {
      const buckets = { all: [], graph: [] };
      const current = moment(this.from.value);
      const to = new Date(this.to.value);
      const future = moment().endOf("month");
      while (current < to) {
        const bucketItem = current.format("YYYY-MM");
        if (current < future) {
          buckets.graph.push(bucketItem);
        }
        buckets.all.push(bucketItem);
        current.add(1, "month");
      }
      return buckets;
    },
    years() {
      return _.range(
        new Date(this.from.value).getFullYear(),
        new Date(this.to.value).getFullYear() + 1,
        1
      );
    },
    allBucketsPerYear() {
      return _.groupBy(this.buckets.all, timestamp => timestamp.slice(0, 4));
    },
    creationStoppedChartData() {
      return {
        labels: this.buckets.graph,
        datasets: this.createChartDataForStatus(
          {
            status: "created",
            label: "Créations",
            color: "rgb(181,234,89)"
          },
          {
            status: "stopped_by_createdAt",
            label: "Abandons",
            color: "rgb(255,0,0)"
          },
          "createdAt"
        )
      };
    },
    toAssignResearchStartedChartData() {
      return {
        labels: this.buckets.graph,
        datasets: this.createChartDataForStatus(
          {
            status: "toAssign",
            label: "À répartir",
            color: "rgb(111,95,183)"
          },
          {
            status: "researchStarted_by_toAssign",
            label: "Recherche",
            color: "rgb(183,171,95)"
          },
          "progress.toAssign"
        )
      };
    },
    leaseFetchedSentToALChartData() {
      return {
        labels: this.buckets.graph,
        datasets: this.createChartDataForStatus(
          {
            status: "leaseFetched",
            label: "Signature du bail",
            color: "rgb(58,188,227)"
          },
          {
            status: "sentToAL_by_leaseFetched",
            label: "Envoyé à AL",
            color: "rgb(190,58,227)"
          },
          "progress.leaseFetched"
        )
      };
    },
    paidChartData() {
      return {
        labels: this.buckets.graph,
        datasets: this.createChartDataForStatus(
          {
            status: "sentToAL",
            label: "Envoyé à AL",
            color: "rgb(82,119,22)"
          },
          {
            status: "paid_by_sentToAL",
            label: "Payé par AL",
            color: "rgb(125,190,20)"
          },
          "progress.sentToAL"
        )
      };
    },
    proportionChartData() {
      if (!this.rawData) {
        return { labels: this.buckets.graph, datasets: [] };
      }

      const computeFn = (numerator, denominator) => ts => {
        const numeratorStat = (
          this.rawDataByTS.stats[numerator][ts] || { count: 0 }
        ).count;
        const denominatorStat = (
          this.rawDataByTS.stats[denominator][ts] || { count: 0 }
        ).count;
        if (denominatorStat === 0) {
          return 0; // We can't divide by 0
        }
        return Math.round((numeratorStat / denominatorStat) * 100);
      };

      return {
        labels: this.buckets.graph,
        datasets: [
          {
            label: "À répartir / créations",
            compute: computeFn("toAssign_by_createdAt", "created"),
            color: "rgb(183,179,95)"
          },
          {
            label: "Envoyés à AL / à répartir",
            compute: computeFn("sentToAL_by_toAssign", "toAssign"),
            color: "rgb(114,81,124)"
          },
          {
            label: "Envoyés à AL / créations",
            compute: computeFn("sentToAL_by_createdAt", "created"),
            color: "rgb(171,12,95)"
          }
        ].map(dataset => {
          return {
            label: dataset.label,
            backgroundColor: dataset.color,
            data: this.buckets.graph.map(ts => {
              return {
                x: ts,
                y: dataset.compute(ts)
              };
            })
          };
        })
      };
    },
    b2cChartData() {
      if (!this.rawData) {
        return { labels: this.buckets.graph, datasets: [] };
      }

      return {
        labels: this.buckets.graph,
        datasets: [
          { label: "Vu", type: "seen", color: "rgb(241,106,7)" },
          { label: "Sauvegardé", type: "saved", color: "rgb(198,255,106)" }
        ].map(({ label, type, color }) => ({
          label,
          backgroundColor: color,
          borderColor: color,
          borderWidth: 1,
          data: _(this.rawData.b2c[type])
            .map(d => ({
              x: d._id,
              y: d.count,
              trigrams: d.trigrams
            }))
            .sortBy("x")
            .value()
        }))
      };
    }
  }
};
</script>

<style scoped></style>
